import { MetricCard } from '@/components/ui/metric-card'
import type { EngineMetrics } from '@/lib/types'

const MS_DECIMALS = 2
const MS_PER_SECOND = 1000
const BYTES_PER_MB = 1_048_576

function fmtMs(seconds: number): string {
  return `${(seconds * MS_PER_SECOND).toFixed(MS_DECIMALS)}ms`
}

function fmtMb(bytes: number): string {
  return `${(bytes / BYTES_PER_MB).toFixed(1)}MB`
}

function fmtRate(current: number, previous: number | undefined, intervalSeconds: number): string {
  if (previous === undefined) return current.toLocaleString()
  const delta = current - previous
  if (delta < 0) return '0/s'
  const rate = delta / intervalSeconds
  return `${rate.toFixed(1)}/s`
}

interface MetricsGridProps {
  metrics: EngineMetrics | undefined
  previousMetrics: EngineMetrics | undefined
  intervalSeconds: number
  isLoading: boolean
  lastUpdated: Date | null
}

export function MetricsGrid({
  metrics,
  previousMetrics,
  intervalSeconds,
  isLoading,
  lastUpdated,
}: MetricsGridProps): React.JSX.Element {
  if (isLoading || !metrics) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {['s0', 's1', 's2', 's3', 's4', 's5', 's6', 's7'].map((id) => (
          <div
            key={id}
            className="h-24 animate-pulse rounded-xl border border-surface-3 bg-surface-0"
          />
        ))}
      </div>
    )
  }

  const secondsAgo = lastUpdated
    ? Math.round((Date.now() - lastUpdated.getTime()) / MS_PER_SECOND)
    : null

  return (
    <div className="space-y-2">
      {secondsAgo !== null && (
        <p className="text-xs text-text-tertiary">Last updated: {secondsAgo}s ago</p>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Accepted"
          value={fmtRate(
            metrics.messages_accepted,
            previousMetrics?.messages_accepted,
            intervalSeconds,
          )}
          sub={`${metrics.messages_accepted.toLocaleString()} total`}
        />
        <MetricCard
          label="Delivered"
          value={fmtRate(
            metrics.messages_delivered,
            previousMetrics?.messages_delivered,
            intervalSeconds,
          )}
          sub={`${metrics.messages_delivered.toLocaleString()} total`}
          variant="success"
        />
        <MetricCard
          label="Bounced"
          value={fmtRate(
            metrics.messages_bounced,
            previousMetrics?.messages_bounced,
            intervalSeconds,
          )}
          sub={`${metrics.messages_bounced.toLocaleString()} total`}
          variant={metrics.messages_bounced > 0 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Deferred"
          value={fmtRate(
            metrics.messages_deferred,
            previousMetrics?.messages_deferred,
            intervalSeconds,
          )}
          sub={`${metrics.messages_deferred.toLocaleString()} total`}
          variant={metrics.messages_deferred > 0 ? 'warning' : 'default'}
        />
        <MetricCard label="Delivery p50" value={fmtMs(metrics.delivery_duration_p50)} />
        <MetricCard label="Delivery p99" value={fmtMs(metrics.delivery_duration_p99)} />
        <MetricCard label="DKIM Sign p50" value={fmtMs(metrics.dkim_sign_p50)} />
        <MetricCard
          label="Compose p50"
          value={fmtMs(metrics.compose_p50)}
          sub={`Memory: ${fmtMb(metrics.memory_bytes)}`}
        />
      </div>
    </div>
  )
}
